// packages/0-foundation/core.module/src/v1/_universal/lvl_1/logger.part/classes/consola-logger-base.class.ts

import type { ConsolaInstance } from "consola";
import { isRecord, type CrashSink } from "../../utils";
import type { ILogger } from "../types";
import { maskSensitiveData, serializeMeta } from "../utils";

export class ConsolaLoggerBase implements ILogger {
  public readonly agentName: string;
  public readonly omitKeys: string[];
  private readonly maskSensitive: boolean;
  private readonly crashSink: CrashSink | undefined;
  private readonly consola: ConsolaInstance;

  constructor(props: {
    agentName: string;
    consola: ConsolaInstance;
    omitKeys?: string[];
    maskSensitive?: boolean;
    crashSink?: CrashSink;
  }) {
    this.agentName = props.agentName;
    this.omitKeys = props.omitKeys ?? [];
    this.maskSensitive = props.maskSensitive ?? false;
    this.crashSink = props.crashSink;
    this.consola = props.consola.withTag(props.agentName);
  }

  private serialize(data: unknown): unknown {
    const meta = serializeMeta(data, this.omitKeys);
    return this.maskSensitive ? maskSensitiveData(meta) : meta;
  }

  private reportError(message: string, data: unknown): void {
    if (!this.crashSink) return;
    const nested = isRecord(data) ? data["error"] : undefined;
    const err =
      data instanceof Error
        ? data
        : nested instanceof Error
          ? nested
          : new Error(message);
    try {
      this.crashSink.captureException(err, { message, data });
    } catch {
      // сбой репортера не должен ломать логирование
    }
  }

  public fatal(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.fatal(message);
    } else {
      this.consola.fatal(message, this.serialize(data));
    }
    this.reportError(message, data);
  }

  public error(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.error(message);
    } else {
      this.consola.error(message, this.serialize(data));
    }
    this.reportError(message, data);
  }

  public warn(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.warn(message);
    } else {
      this.consola.warn(message, this.serialize(data));
    }
  }

  public log(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.log(message);
    } else {
      this.consola.log(message, this.serialize(data));
    }
  }

  public info(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.info(message);
    } else {
      this.consola.info(message, this.serialize(data));
    }
  }

  public success(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.success(message);
    } else {
      this.consola.success(message, this.serialize(data));
    }
  }

  public fail(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.fail(message);
    } else {
      this.consola.fail(message, this.serialize(data));
    }
  }

  public ready(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.ready(message);
    } else {
      this.consola.ready(message, this.serialize(data));
    }
  }

  public start(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.start(message);
    } else {
      this.consola.start(message, this.serialize(data));
    }
  }

  public box(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.box(message);
    } else {
      this.consola.box(message, this.serialize(data));
    }
  }

  public debug(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.debug(message);
    } else {
      this.consola.debug(message, this.serialize(data));
    }
  }

  public trace(message: string, data?: unknown): void {
    if (data === undefined) {
      this.consola.trace(message);
    } else {
      this.consola.trace(message, this.serialize(data));
    }
  }
}
